import { useEffect } from 'react';
import { X } from 'lucide-react';
import type { plans } from '@/lib/client';
import { usePlansStore } from '@/stores/plansStore';
import { PlanDag } from './PlanDag';
import { StepPanel } from './StepPanel';

/** The plan's graph in a large view over the page: the steps and how they wait on each other, the selected one beside it. */
export function PlanGraphDialog({ plan, editable, onClose }: { plan: plans.PlanView; editable: boolean; onClose: () => void }) {
  const selectedStepKey = usePlansStore((s) => s.selectedStepKey);
  const selected = plan.steps.find((step) => step.key === selectedStepKey) ?? null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex bg-black/70 p-4 sm:p-6" role="dialog" aria-modal="true" aria-label="Plan graph" onClick={onClose}>
      <div className="flex min-h-0 w-full flex-col gap-3 rounded-xl border border-border bg-background p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="truncate text-sm font-semibold text-foreground">{plan.title}: how the steps wait on each other</h2>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" aria-label="Close the graph"><X className="h-4 w-4" /></button>
        </div>
        <div className={selected ? 'grid min-h-0 flex-1 gap-4 lg:grid-cols-[1fr_400px]' : 'flex min-h-0 flex-1'}>
          <PlanDag steps={plan.steps} />
          {selected && <StepPanel plan={plan} step={selected} editable={editable} />}
        </div>
      </div>
    </div>
  );
}
